<template>
  <div class="rs2-page">
    <div class="rs2-filters">
      <v-select
        v-model="filters.partner_id"
        :items="partners"
        item-text="name"
        item-value="id"
        label="Partner"
        outlined
        dense
        hide-details
        :disabled="loading"
        class="rs2-field rs2-field--wide"
        @change="onPartnerChange"
      />

      <v-menu
        v-model="fromMenu"
        :close-on-content-click="false"
        offset-y
        min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            :value="filters.from_date"
            label="From"
            prepend-inner-icon="mdi-calendar-start"
            readonly
            outlined
            dense
            hide-details
            :disabled="loading || !filters.partner_id"
            class="rs2-field rs2-field--date"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="filters.from_date"
          no-title
          @input="fromMenu = false"
        />
      </v-menu>

      <v-menu
        v-model="toMenu"
        :close-on-content-click="false"
        offset-y
        min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            :value="filters.to_date"
            label="To"
            prepend-inner-icon="mdi-calendar-end"
            readonly
            outlined
            dense
            hide-details
            :disabled="loading || !filters.partner_id"
            class="rs2-field rs2-field--date"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="filters.to_date"
          no-title
          @input="toMenu = false"
        />
      </v-menu>

      <v-btn
        color="accent white--text"
        :disabled="!filters.partner_id || loading"
        :loading="loading"
        @click="runSearch"
      >
        Search
      </v-btn>
      <div class="d-flex justify-end">
        <v-btn
          class="accent white--text"
          :disabled="!working"
          @click="downloadWorking"
        >
          Export CSV
        </v-btn>
        <v-btn
          color="accent white--text"
          class="ml-2"
          :disabled="!chargeable"
          @click="goToInvoices"
        >
          Generate Invoice
        </v-btn>
      </div>
    </div>

    <div v-if="loading" class="rs2-state">
      <p class="rs2-state__text">
        Loading Revenue Summary for {{ getPartnerName(filters.partner_id) }}…
      </p>
    </div>

    <v-card v-else-if="!working" outlined class="rs2-state rs2-state--card">
      <p class="rs2-state__text">
        {{
          filters.partner_id
            ? "Set the collection window, then hit Search."
            : "Collections come from every place money arrives — admission plans, advance fees, student invoices, excess payments and add-ons."
        }}
      </p>
      <v-btn
        v-if="filters.partner_id"
        small
        depressed
        color="primary"
        class="mt-4"
        @click="runSearch"
      >
        <v-icon small left>mdi-magnify</v-icon>
        Search
      </v-btn>
    </v-card>

    <v-card
      v-else-if="!working.totals.collection_count"
      outlined
      class="rs2-state rs2-state--card"
    >
      <v-icon size="44" color="grey lighten-1">mdi-cash-remove</v-icon>
      <h3 class="rs2-state__title">Nothing collected in this window</h3>
      <p class="rs2-state__text">
        {{ working.period.label }} has nothing to invoice for
        {{ working.partner.name }}.
      </p>
    </v-card>

    <template v-else>
      <div class="rs2-totals">
        <div class="rs2-total">
          <span class="rs2-total__label">Collections</span>
          <span class="rs2-total__value">{{
            money(working.totals.collection_amount)
          }}</span>
          <span class="rs2-total__meta">
            {{ working.totals.collection_count }} payments ·
            {{ working.totals.student_count }} students
          </span>
        </div>
        <v-icon class="rs2-arrow">mdi-chevron-right</v-icon>
        <div class="rs2-total">
          <span class="rs2-total__label">Gross Charge</span>
          <span class="rs2-total__value">{{
            money(working.totals.gross_amount)
          }}</span>
          <span class="rs2-total__meta">
            {{ number(working.totals.share_percentage) }}% of collections
          </span>
        </div>
        <v-icon class="rs2-arrow">mdi-minus</v-icon>
        <div class="rs2-total">
          <span class="rs2-total__label">GST</span>
          <span class="rs2-total__value">{{
            money(working.totals.gst_amount)
          }}</span>
          <span class="rs2-total__meta">on inclusive categories</span>
        </div>
        <v-icon class="rs2-arrow">mdi-equal</v-icon>
        <div class="rs2-total rs2-total--net">
          <span class="rs2-total__label">Net Payable</span>
          <span class="rs2-total__value">{{
            money(working.totals.net_amount)
          }}</span>
          <span class="rs2-total__meta">to {{ working.partner.name }}</span>
        </div>
      </div>

      <div class="rs2-buckets">
        <div
          v-for="pool in working.pools"
          :key="pool.key"
          class="rs2-bucket"
          :class="`rs2-bucket--${pool.key}`"
        >
          <span class="rs2-bucket__label">{{ pool.label }}</span>
          <span class="rs2-bucket__amount">{{ money(pool.amount) }}</span>
          <span class="rs2-bucket__meta">
            {{ pool.student_count }} students ·
            {{ pool.collection_count }} payments
          </span>
          <span class="rs2-bucket__note">
            {{
              pool.basis === "enrolment"
                ? "enrolled in this window · valued at total admission fee"
                : "collected in this window"
            }}
          </span>
        </div>
      </div>

      <v-card outlined class="rs2-card">
        <div class="rs2-card__head">
          <div>
            <h3 class="rs2-card__title">Category working</h3>
            <p class="rs2-card__sub">
              {{ working.partner.name }} · {{ working.period.label }}
            </p>
          </div>
        </div>

        <div class="rs2-scroll">
          <table class="rs2-table">
            <thead>
              <tr>
                <th class="rs2-th">Service Category</th>
                <th class="rs2-th">Charged On</th>
                <th class="rs2-th rs2-th--num">Base</th>
                <th class="rs2-th rs2-th--num">Rate</th>
                <th class="rs2-th rs2-th--num">Gross</th>
                <th class="rs2-th rs2-th--num">GST</th>
                <th class="rs2-th rs2-th--num">Net</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="line in working.categories"
                :key="line.key"
                :class="{ 'rs2-row--empty': !Number(line.gross_amount) }"
              >
                <td class="rs2-td">
                  <span class="rs2-cat">{{ line.name }}</span>
                  <span class="rs2-sub">HSN {{ line.hsn }}</span>
                </td>
                <td class="rs2-td">
                  <span>{{ line.charged_on }}</span>
                  <span
                    class="rs2-source"
                    :class="`rs2-source--${line.rate_source}`"
                  >
                    {{ sourceLabel(line.rate_source) }}
                  </span>
                </td>
                <td class="rs2-td rs2-td--num">
                  {{ money(line.base_amount) }}
                  <span class="rs2-sub">{{ line.student_count }} students</span>
                </td>
                <td class="rs2-td rs2-td--num">
                  {{ number(line.rate) }}%
                  <span class="rs2-sub">
                    GST {{ number(line.gst_percentage) }}%
                    {{ gstLabel(line) }}
                  </span>
                </td>
                <td class="rs2-td rs2-td--num">
                  {{ money(line.gross_amount) }}
                </td>
                <td class="rs2-td rs2-td--num">
                  {{ money(line.gst_amount) }}
                  <span v-if="Number(line.gst_amount) > 0" class="rs2-sub">
                    CGST {{ money(line.cgst_amount) }} · SGST
                    {{ money(line.sgst_amount) }}
                  </span>
                </td>
                <td class="rs2-td rs2-td--num rs2-td--strong">
                  {{ money(line.net_amount) }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td class="rs2-tf" colspan="4">Total</td>
                <td class="rs2-tf rs2-tf--num">
                  {{ money(working.totals.gross_amount) }}
                </td>
                <td class="rs2-tf rs2-tf--num">
                  {{ money(working.totals.gst_amount) }}
                </td>
                <td class="rs2-tf rs2-tf--num rs2-tf--strong">
                  {{ money(working.totals.net_amount) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </v-card>

      <v-card outlined class="rs2-card">
        <div class="rs2-card__head">
          <div>
            <h3 class="rs2-card__title">Reconciliation</h3>
            <p class="rs2-card__sub">
              Students and share per service category. Filter the list below by
              the same category to see exactly who is behind each figure.
            </p>
          </div>
        </div>
        <div class="rs2-recon">
          <div
            v-for="line in working.categories"
            :key="line.key"
            class="rs2-recon__item"
            :class="`rs2-recon__item--${line.key}`"
          >
            <span class="rs2-recon__label">{{ line.name }}</span>
            <span class="rs2-recon__amount">{{
              money(line.gross_amount)
            }}</span>
            <span class="rs2-recon__meta">
              {{ line.student_count }} students · {{ number(line.rate) }}% share
            </span>
            <span class="rs2-recon__meta"
              >on {{ money(line.base_amount) }}</span
            >
          </div>
        </div>
      </v-card>

      <v-card v-if="consolidated" outlined class="rs2-card">
        <div class="rs2-card__head">
          <div>
            <h3 class="rs2-card__title">By partner</h3>
            <p class="rs2-card__sub">
              Every partner whose students {{ partnerName }} is charged for.
              Click a row to filter the list below.
            </p>
          </div>
        </div>

        <div class="rs2-scroll">
          <table class="rs2-table">
            <thead>
              <tr>
                <th class="rs2-th">Partner</th>
                <th class="rs2-th rs2-th--num">Rows</th>
                <th class="rs2-th rs2-th--num">Students</th>
                <th class="rs2-th rs2-th--num">Charged On</th>
                <th class="rs2-th rs2-th--num">Share of base</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in partnerBreakdown.rows"
                :key="row.name"
                class="rs2-row--click"
                :class="{ 'rs2-row--on': sourcePartnerFilter === row.name }"
                @click="
                  sourcePartnerFilter =
                    sourcePartnerFilter === row.name ? null : row.name
                "
              >
                <td class="rs2-td">{{ row.name }}</td>
                <td class="rs2-td rs2-td--num">{{ row.rows }}</td>
                <td class="rs2-td rs2-td--num">{{ row.students }}</td>
                <td class="rs2-td rs2-td--num">{{ money(row.amount) }}</td>
                <td class="rs2-td rs2-td--num">{{ row.share.toFixed(2) }}%</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td class="rs2-tf">All partners</td>
                <td class="rs2-tf rs2-td--num">
                  {{ partnerBreakdown.rows_total }}
                </td>
                <td class="rs2-tf rs2-td--num">
                  {{ partnerBreakdown.students_total }}
                </td>
                <td class="rs2-tf rs2-td--num">
                  {{ money(partnerBreakdown.amount_total) }}
                </td>
                <td class="rs2-tf rs2-td--num">100.00%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </v-card>

      <v-card outlined class="rs2-card">
        <div class="rs2-card__head">
          <div>
            <h3 class="rs2-card__title">Charged rows</h3>
            <p class="rs2-card__sub">
              <template v-if="consolidated">
                Every partner's students, consolidated — {{ partnerName }} runs
                the programme for the university.
              </template>
              <template v-else>
                What each category is charged on, for the students
                {{ partnerName }} sourced. An enrolment is one row per student,
                valued at everything they have paid towards admission.
              </template>
            </p>
          </div>
          <div class="rs2-card__tools">
            <v-select
              v-if="consolidated"
              v-model="sourcePartnerFilter"
              :items="sourcePartnerItems"
              item-text="text"
              item-value="value"
              label="Partner"
              outlined
              dense
              hide-details
              class="rs2-tool"
            />
            <v-select
              v-model="bucketFilter"
              :items="bucketFilterItems"
              item-text="text"
              item-value="value"
              label="Category"
              outlined
              dense
              hide-details
              class="rs2-tool"
            />
            <v-text-field
              v-model="search"
              placeholder="Filter"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              class="rs2-tool"
            />
          </div>
        </div>

        <div class="rs2-filtered">
          <span>
            <strong>{{ filteredSummary.students }}</strong> students ·
            <strong>{{ filteredSummary.rows }}</strong> rows ·
            <strong>{{ money(filteredSummary.total) }}</strong>
          </span>
          <span v-if="selectedCategoryLine" class="rs2-filtered__tie">
            {{ selectedCategoryLine.name }} is invoiced on
            <strong>{{ money(selectedCategoryLine.base_amount) }}</strong>
            over
            <strong>{{ selectedCategoryLine.student_count }}</strong>
            students<template v-if="!consolidated"
              >; listed here are the
              <strong>{{ filteredSummary.students }}</strong>
              {{ partnerName }} sourced</template
            >.
            <v-icon v-if="tiesOut" x-small color="success" class="ml-1">
              mdi-check-circle
            </v-icon>
          </span>
          <span v-else class="rs2-filtered__tie">
            Categories overlap — an enrolment is charged under all of them at
            once, so the filter changes the share column, not the rows.
          </span>
        </div>

        <v-data-table
          :headers="collectionHeaders"
          :items="switchingBucket ? [] : filteredCollections"
          :loading="switchingBucket"
          loading-text="Applying the category…"
          :search="search"
          :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          dense
          class="rs2-data-table"
        >
          <template #[`item.amount`]="{ item }">{{
            money(item.amount)
          }}</template>
          <template #[`item.on_date`]="{ item }">
            {{ date(item.on_date) }}
          </template>
          <template #[`item.kind_label`]="{ item }">
            <span class="rs2-tag">{{ item.kind_label }}</span>
          </template>

          a rendered list, so they carry no key. -->
          <template
            v-for="line in working.categories"
            #[`item.share_${line.key}`]="{ item }"
          >
            <span
              :class="{ 'rs2-share--off': !item.categories.includes(line.key) }"
            >
              {{
                item.categories.includes(line.key)
                  ? money(item[`share_${line.key}`])
                  : "—"
              }}
            </span>
          </template>
        </v-data-table>
      </v-card>
    </template>
  </div>
</template>

<script>
export default {
  name: "Revenue Summary",

  data() {
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      partners: [],
      working: null,
      loading: false,
      search: "",
      bucketFilter: null,
      sourcePartnerFilter: null,
      switchingBucket: false,
      bucketTimer: null,
      fromMenu: false,
      toMenu: false,
      filters: {
        partner_id: null,
        from_date: this.toIso(first),
        to_date: this.toIso(last),
      },
      baseHeaders: [
        { text: "Type", value: "kind_label" },
        { text: "USN", value: "usn_id" },
        { text: "Student", value: "student_name" },
        { text: "Sourced By", value: "partner_name" },
        { text: "Programme", value: "programme" },
        { text: "Fee Type", value: "fee_type" },
        { text: "Reference", value: "reference" },
        { text: "Date", value: "on_date" },
        { text: "Amount", value: "amount", align: "end" },
      ],
    };
  },

  computed: {
    shownCategories() {
      const lines = this.working?.categories || [];
      return this.bucketFilter
        ? lines.filter((line) => line.key === this.bucketFilter)
        : lines;
    },

    collectionHeaders() {
      const shares = this.shownCategories.map((line) => ({
        text: `${this.shortCategory(line.key)} ${this.number(line.rate)}%`,
        value: `share_${line.key}`,
        align: "end",
        sortable: false,
      }));

      return [...this.baseHeaders, ...shares];
    },

    categoryRates() {
      return Object.fromEntries(
        (this.working?.categories || []).map((line) => [
          line.key,
          Number(line.rate) || 0,
        ]),
      );
    },

    chargeable() {
      return Number(this.working?.totals?.gross_amount || 0) > 0;
    },

    consolidated() {
      return ["Self", "Institute"].includes(this.working?.partner?.type);
    },

    partnerCollections() {
      const rows = this.working?.collections || [];
      return this.consolidated ? rows : rows.filter((row) => row.sourced);
    },

    partnerBreakdown() {
      const groups = new Map();

      this.partnerCollections.forEach((row) => {
        const name = row.partner_name || "Unassigned";
        if (!groups.has(name)) {
          groups.set(name, { name, rows: 0, students: new Set(), amount: 0 });
        }
        const group = groups.get(name);
        group.rows += 1;
        if (row.usn_id) group.students.add(row.usn_id);
        group.amount += Number(row.amount) || 0;
      });

      const total = [...groups.values()].reduce(
        (sum, group) => sum + group.amount,
        0,
      );

      const rows = [...groups.values()]
        .map((group) => ({
          name: group.name,
          rows: group.rows,
          students: group.students.size,
          amount: group.amount,
          share: total ? (group.amount / total) * 100 : 0,
        }))
        .sort((a, b) => b.amount - a.amount);

      return {
        rows,
        rows_total: rows.reduce((sum, row) => sum + row.rows, 0),
        students_total: new Set(
          this.partnerCollections.map((row) => row.usn_id).filter(Boolean),
        ).size,
        amount_total: total,
      };
    },

    sourcePartnerItems() {
      const rows = this.bucketFilter
        ? this.partnerCollections.filter((row) =>
            (row.categories || []).includes(this.bucketFilter),
          )
        : this.partnerCollections;

      const counts = new Map();
      rows.forEach((row) => {
        const name = row.partner_name || "Unassigned";
        counts.set(name, (counts.get(name) || 0) + 1);
      });

      const items = [...counts.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([name, count]) => ({
          text: `${name} — ${count} rows`,
          value: name,
        }));

      return [
        { text: `All partners (${rows.length} rows)`, value: null },
        ...items,
      ];
    },

    bucketFilterItems() {
      const rows = this.partnerCollections;

      return [
        { text: `All categories (${rows.length} rows)`, value: null },
        ...(this.working?.categories || []).map((line) => {
          const matching = rows.filter((row) =>
            (row.categories || []).includes(line.key),
          ).length;

          return {
            text: `${line.name} — ${matching} rows`,
            value: line.key,
          };
        }),
      ];
    },

    filteredCollections() {
      let rows = this.partnerCollections;

      if (this.bucketFilter) {
        rows = rows.filter((row) =>
          (row.categories || []).includes(this.bucketFilter),
        );
      }

      if (this.consolidated && this.sourcePartnerFilter) {
        rows = rows.filter(
          (row) =>
            (row.partner_name || "Unassigned") === this.sourcePartnerFilter,
        );
      }

      return rows.map((row) => {
        const shares = {};
        (row.categories || []).forEach((key) => {
          shares[`share_${key}`] =
            (Number(row.amount) || 0) * ((this.categoryRates[key] || 0) / 100);
        });
        return { ...row, ...shares };
      });
    },

    partnerName() {
      const partner = this.partners.find(
        (row) => row.id === this.filters.partner_id,
      );
      return partner ? partner.name : "this partner";
    },

    selectedCategoryLine() {
      if (!this.bucketFilter) return null;
      return (this.working?.categories || []).find(
        (line) => line.key === this.bucketFilter,
      );
    },

    tiesOut() {
      const line = this.selectedCategoryLine;
      if (!line) return true;
      return (
        this.filteredSummary.students === line.student_count &&
        Math.abs(this.filteredSummary.total - Number(line.base_amount)) < 0.5
      );
    },

    filteredSummary() {
      const rows = this.filteredCollections;
      const students = new Set(rows.map((row) => row.usn_id).filter(Boolean));
      const total = rows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
      return { rows: rows.length, students: students.size, total };
    },
  },

  watch: {
    bucketFilter() {
      this.showRowsLoader();
    },

    sourcePartnerFilter() {
      this.showRowsLoader();
    },
  },

  beforeDestroy() {
    clearTimeout(this.bucketTimer);
  },

  async created() {
    this.$store.commit("setBreadcrumbs", [
      { text: "Revenue Split", to: { name: "Revenue Setup" } },
      { text: "Revenue Summary" },
    ]);

    if (this.$route.query.partner_id) {
      this.loading = true;
      this.filters.partner_id = Number(this.$route.query.partner_id);
    }

    await this.initialise();
    await this.runSearch();
  },

  methods: {
    async initialise() {
      await this.getPartners();
    },

    onPartnerChange() {
      this.working = null;
    },

    async runSearch() {
      if (!this.filters.partner_id) return;
      await this.load();
    },

    async getPartners() {
      try {
        const res = await this.$axios.get("partners");
        this.partners = (res.data.data || []).filter(
          (partner) => partner.type !== "Sponsor",
        );
      } catch (err) {
        this.notifyError(err, "Unable to load partners");
      }
    },

    async load() {
      if (!this.filters.partner_id) return;

      this.loading = true;
      try {
        const params = {
          partner_id: this.filters.partner_id,
          from_date: this.filters.from_date,
          to_date: this.filters.to_date,
        };

        const res = await this.$axios.get("/revenue_workings", { params });
        this.working = res.data.data;
      } catch (err) {
        this.working = null;
        this.notifyError(err, "Unable to work out this period");
      } finally {
        this.loading = false;
      }
    },

    showRowsLoader() {
      if (!this.working) return;

      clearTimeout(this.bucketTimer);
      this.switchingBucket = true;

      this.bucketTimer = setTimeout(() => {
        this.switchingBucket = false;
      }, 350);
    },

    goToInvoices() {
      this.$router.push({
        name: "Revenue Invoices",
        query: {
          partner_id: this.filters.partner_id,
          from_date: this.filters.from_date,
          to_date: this.filters.to_date,
        },
      });
    },

    downloadWorking() {
      const categoryRows = [
        [
          "Service Category",
          "HSN",
          "Charged On",
          "Base",
          "Rate %",
          "GST %",
          "GST Type",
          "Gross",
          "GST",
          "Net",
        ],
        ...this.working.categories.map((line) => [
          line.name,
          line.hsn,
          line.charged_on,
          line.base_amount,
          line.rate,
          line.gst_percentage,
          line.gst_type,
          line.gross_amount,
          line.gst_amount,
          line.net_amount,
        ]),
      ];

      const collectionRows = [
        [
          "Type",
          "USN",
          "Application ID",
          "Student",
          "Sourced By",
          "Programme",
          "Fee Type",
          "Reference",
          "Date",
          "Amount",
          ...this.shownCategories.map((line) => `${line.name} ${line.rate}%`),
        ],
        ...this.filteredCollections.map((row) => [
          row.kind_label,
          row.usn_id,
          row.application_id,
          row.student_name,
          row.partner_name,
          row.programme,
          row.fee_type,
          row.reference,
          row.on_date,
          row.amount,
          ...this.shownCategories.map((line) =>
            (row.categories || []).includes(line.key)
              ? row[`share_${line.key}`]
              : "",
          ),
        ]),
      ];

      const csv = [...categoryRows, [], ...collectionRows]
        .map((row) =>
          row
            .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
            .join(","),
        )
        .join("\r\n");

      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      link.download = `revenue_summary_${this.filters.from_date}_${this.filters.to_date}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },

    sourceLabel(source) {
      return (
        {
          batch: "batch rate",
          partner: "partner rate",
          fee_codes: "fee code rates",
          fee_codes_batch: "fee code rates (batch)",
        }[source] || "default rate"
      );
    },

    gstLabel(line) {
      if (!Number(line.gst_percentage)) return "";
      return line.gst_type === "Inclusive" ? "incl." : "excl.";
    },

    poolColor(key) {
      return (
        {
          admission: "deep-purple",
          academic_support: "primary",
          online_support: "teal",
          exam_support: "blue-grey",
        }[key] || "grey"
      );
    },

    shortCategory(key) {
      return (
        {
          admission: "Admission",
          academic_support: "Academic",
          online_support: "Online Edu",
          exam_support: "Exam",
        }[key] || key
      );
    },

    money(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    number(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      });
    },

    date(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    getPartnerName(partner_id) {
      const partner = this.partners.find((p) => p.id === partner_id);
      return partner ? partner.name : "—";
    },

    toIso(date) {
      const offset = date.getTimezoneOffset() * 60000;
      return new Date(date.getTime() - offset).toISOString().slice(0, 10);
    },

    notifyError(err, fallback) {
      this.$store.commit("showSnackbar", {
        message: err?.response?.data?.message || fallback,
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.rs2-page {
  --rs2-line: #e4e7ec;
  --rs2-ink: #101828;
  --rs2-ink-2: #475467;
  --rs2-ink-3: #98a2b3;
  padding: 16px 20px 40px;
  min-height: 100%;
}

.rs2-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.rs2-header__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--rs2-ink);
  letter-spacing: -0.2px;
}

.rs2-header__sub {
  margin: 4px 0 0;
  max-width: 620px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--rs2-ink-2);
}

.rs2-filters {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
}

.rs2-field {
  max-width: 170px;
  flex: 0 1 170px;
}

.rs2-field--wide {
  max-width: 210px;
  flex: 0 1 210px;
}

.rs2-field--date {
  max-width: 150px;
  flex: 0 1 150px;
}

.rs2-check {
  margin: 0;
  padding: 0;
}

.rs2-check ::v-deep .v-label {
  font-size: 12.5px;
}

.rs2-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 44px 20px;
}

.rs2-state--card {
  margin-top: 12px;
  border-radius: 10px;
}

.rs2-state__title {
  margin: 14px 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--rs2-ink);
}

.rs2-state__text {
  margin: 0;
  max-width: 560px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--rs2-ink-2);
}

.rs2-totals {
  display: flex;
  align-items: stretch;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 14px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid var(--rs2-line);
  border-radius: 10px;
}

.rs2-total {
  flex: 1 1 150px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px 10px;
}

.rs2-total--net {
  border-left: 3px solid var(--v-primary-base, #00aff1);
}

.rs2-total__label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--rs2-ink-2);
}

.rs2-total__value {
  font-size: 19px;
  font-weight: 700;
  color: var(--rs2-ink);
  font-variant-numeric: tabular-nums;
}

.rs2-total__meta {
  font-size: 11.5px;
  color: var(--rs2-ink-3);
}

.rs2-arrow {
  align-self: center;
  color: var(--rs2-ink-3) !important;
}

.rs2-buckets {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.rs2-bucket {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid var(--rs2-line);
  border-radius: 10px;
  border-top: 3px solid #98a2b3;
}

.rs2-bucket--enrolment_sourced {
  border-top-color: #7a5af8;
}

.rs2-bucket--enrolment {
  border-top-color: #00aff1;
}

.rs2-bucket--higher_semester {
  border-top-color: #12b76a;
}

.rs2-bucket--other_revenue {
  border-top-color: #98a2b3;
}

.rs2-bucket__note {
  font-size: 10.5px;
  line-height: 1.35;
  color: var(--rs2-ink-3);
}

.rs2-bucket__label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--rs2-ink-2);
}

.rs2-bucket__amount {
  font-size: 18px;
  font-weight: 700;
  color: var(--rs2-ink);
  font-variant-numeric: tabular-nums;
}

.rs2-bucket__meta {
  font-size: 11.5px;
  color: var(--rs2-ink-3);
}

.rs2-card {
  margin-top: 12px;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

.rs2-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border-bottom: 1px solid var(--rs2-line);
}

.rs2-card__title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--rs2-ink);
}

.rs2-card__sub {
  margin: 3px 0 0;
  max-width: 560px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--rs2-ink-2);
}

.rs2-card__tools {
  display: flex;
  gap: 8px;
}

.rs2-tool {
  max-width: 170px;
  flex: 0 1 170px;
}

.rs2-scroll {
  overflow-x: auto;
}

.rs2-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
}

.rs2-th {
  padding: 8px 14px;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--rs2-ink-2);
  background: #f9fafb;
  border-bottom: 1px solid var(--rs2-line);
  white-space: nowrap;
}

.rs2-th--num {
  text-align: right;
}

.rs2-td {
  padding: 11px 14px;
  font-size: 12.5px;
  color: var(--rs2-ink-2);
  border-bottom: 1px solid #f4f5f7;
  vertical-align: top;
}

.rs2-td--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rs2-td--strong {
  font-weight: 700;
  color: var(--rs2-ink);
}

.rs2-row--empty {
  opacity: 0.55;
}

.rs2-cat {
  display: block;
  font-weight: 600;
  color: var(--rs2-ink);
}

.rs2-sub {
  display: block;
  font-size: 11px;
  color: var(--rs2-ink-3);
}

.rs2-source {
  display: inline-block;
  margin-top: 3px;
  padding: 0 5px;
  border-radius: 4px;
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: #eef2f6;
  color: #344054;
}

.rs2-source--default {
  background: #fef0c7;
  color: #b54708;
}

.rs2-tf {
  padding: 11px 14px;
  font-size: 12.5px;
  font-weight: 700;
  color: var(--rs2-ink);
  background: #f9fafb;
  border-top: 1px solid var(--rs2-line);
}

.rs2-tf--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rs2-tf--strong {
  font-size: 13.5px;
}

.rs2-recon {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1px;
  background: var(--rs2-line);
}

.rs2-share--off {
  color: #d0d5dd;
}

.rs2-filtered {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 8px 16px;
  border-bottom: 1px solid var(--rs2-line);
  background: #fbfcfd;
  font-size: 12.5px;
  color: var(--rs2-ink-2);
}

.rs2-filtered__tie {
  color: var(--rs2-ink-3);
  font-size: 12px;
}

.rs2-filtered strong {
  color: var(--rs2-ink);
  font-variant-numeric: tabular-nums;
}

.rs2-recon__item {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 12px 16px;
  background: #fff;
}

.rs2-recon__label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--rs2-ink-2);
}

.rs2-recon__amount {
  font-size: 15px;
  font-weight: 700;
  color: var(--rs2-ink);
  font-variant-numeric: tabular-nums;
}

.rs2-recon__meta {
  font-size: 11px;
  color: var(--rs2-ink-3);
}

.rs2-tag {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  background: #eef2f6;
  color: #344054;
  font-size: 10.5px;
  font-weight: 600;
  white-space: nowrap;
}

.rs2-data-table ::v-deep th {
  white-space: nowrap;
}

@media (max-width: 600px) {
  .rs2-page {
    padding: 12px 10px 40px;
  }

  .rs2-filters {
    position: static;
  }

  .rs2-field,
  .rs2-field--wide,
  .rs2-field--date,
  .rs2-tool {
    max-width: none;
    flex: 1 1 100%;
  }

  .rs2-arrow {
    display: none;
  }
}

.rs2-row--click {
  cursor: pointer;
}

.rs2-row--click:hover {
  background: #f9fafb;
}

.rs2-row--on {
  background: #eff8ff;
}

.rs2-tf {
  padding: 10px 14px;
  font-size: 12.5px;
  font-weight: 700;
  color: #101828;
  background: #f9fafb;
  border-top: 1px solid #e4e7ec;
  white-space: nowrap;
}
</style>
